import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { centrePortalApi, type Centre } from '@/api/centresApi';
import {
  getCentreAccessToken,
  setCentreAccessToken,
  setCentreRefreshToken,
  removeCentreTokens,
} from '@/utils/storage';

interface CentreSummary {
  id: string;
  name: string;
  status: string;
  /** Set when status is "rejected"/changes-requested — shown on the dashboard. */
  rejection_reason?: string | null;
}

interface CentreAuthContextType {
  centre: CentreSummary | null;
  listing: Centre | null;
  loading: boolean;
  isCentreAuthenticated: boolean;
  signInCentre: (email: string, password: string) => Promise<CentreSummary>;
  establishCentreSession: (access: string, refresh: string, centre: CentreSummary) => Promise<void>;
  signOutCentre: () => void;
  refreshListing: () => Promise<void>;
}

const CentreAuthContext = createContext<CentreAuthContextType>({
  centre: null,
  listing: null,
  loading: true,
  isCentreAuthenticated: false,
  signInCentre: async () => ({ id: '', name: '', status: '' }),
  establishCentreSession: async () => {},
  signOutCentre: () => {},
  refreshListing: async () => {},
});

export const useCentreAuth = () => useContext(CentreAuthContext);

export const CentreAuthProvider = ({ children }: { children: ReactNode }) => {
  const [centre, setCentre] = useState<CentreSummary | null>(null);
  const [listing, setListing] = useState<Centre | null>(null);
  const [loading, setLoading] = useState(true);

  const loadListing = useCallback(async () => {
    const res = await centrePortalApi.getMyListing();
    const data = res.data.centre;
    setListing(data);
    setCentre({ id: data.id, name: data.name, status: data.status ?? '' });
  }, []);

  useEffect(() => {
    const restore = async () => {
      try {
        if (getCentreAccessToken()) {
          await loadListing();
        }
      } catch {
        // token invalid - stay logged out
        removeCentreTokens();
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, [loadListing]);

  const signInCentre = useCallback(
    async (email: string, password: string) => {
      const res = await centrePortalApi.login(email, password);
      setCentreAccessToken(res.data.access);
      setCentreRefreshToken(res.data.refresh);
      setCentre(res.data.centre);
      try {
        await loadListing();
      } catch {
        // listing fetch can be retried in the portal
      }
      return res.data.centre;
    },
    [loadListing],
  );

  const establishCentreSession = useCallback(
    async (access: string, refresh: string, summary: CentreSummary) => {
      setCentreAccessToken(access);
      setCentreRefreshToken(refresh);
      setCentre(summary);
      try {
        await loadListing();
      } catch {
        // listing can be retried in the portal
      }
    },
    [loadListing],
  );

  const signOutCentre = useCallback(() => {
    removeCentreTokens();
    setCentre(null);
    setListing(null);
  }, []);

  const refreshListing = useCallback(async () => {
    try {
      await loadListing();
    } catch {
      // ignore
    }
  }, [loadListing]);

  return (
    <CentreAuthContext.Provider
      value={{
        centre,
        listing,
        loading,
        isCentreAuthenticated: !!centre,
        signInCentre,
        establishCentreSession,
        signOutCentre,
        refreshListing,
      }}
    >
      {children}
    </CentreAuthContext.Provider>
  );
};
